import { useCallback, useLayoutEffect, useRef } from "react";

const throwOnSyncEventHandler = () => {
	throw new Error("You should never call useHandler resulting function during render. Replace it with useCallback.");
};

// @link https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md
/**
 * A custom hook that wraps a handler function and returns a new function with the same signature.
 * The returned function can be used as an event handler or callback function, but it should not be called during render.
 *
 * @template TArgs - The type of arguments that the handler function accepts.
 * @template TResult - The return type of the handler function.
 * @param {(...args: TArgs) => TResult} handler - The handler function to be wrapped.
 * @returns {(...args: TArgs) => TResult} - The wrapped function with the same signature as the handler function.
 */
export function useHandler<TArgs extends ReadonlyArray<unknown> = [], TResult extends void | Promise<unknown> = void>(
	handler: (...args: TArgs) => TResult,
): (...args: TArgs) => TResult;

export function useHandler<THandler extends undefined | ((...args: ReadonlyArray<unknown>) => void | Promise<unknown>)>(
	handler: THandler,
): THandler;

export function useHandler<TArgs extends ReadonlyArray<unknown> = [], TResult extends void | Promise<unknown> = void>(
	handler: undefined | ((...args: TArgs) => TResult),
): undefined | ((...args: TArgs) => TResult);

export function useHandler(
	handler: undefined | ((...args: ReadonlyArray<unknown>) => unknown),
): undefined | ((...args: ReadonlyArray<unknown>) => unknown) {
	const handlerRef = useRef(throwOnSyncEventHandler as typeof handler);
	const stableHandler = useCallback((...args: ReadonlyArray<unknown>) => handlerRef.current!(...args), []);

	useLayoutEffect(() => {
		// assign the real handler during each effect
		handlerRef.current = handler;
	});

	return handler == null ? undefined : stableHandler;
}
